import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StatisticsFilters, filtersFromSearch, filtersToSearch } from '../../utils/period';
import { errorMessage } from '../../services/statisticsService';

/**
 * Period + campus filters, kept in the URL so a view can be linked to and the
 * filters survive moving between statistics screens.
 */
export function useStatisticsFilters(): [StatisticsFilters, (next: Partial<StatisticsFilters>) => void] {
  const [search, setSearch] = useSearchParams();
  const filters = filtersFromSearch(search);
  const update = useCallback(
    (next: Partial<StatisticsFilters>) => {
      const merged = { ...filtersFromSearch(search), ...next };
      const params = new URLSearchParams(filtersToSearch(merged));
      // Keep anything else the page put in the URL (e.g. a selected route).
      for (const [key, value] of search.entries()) {
        if (!['preset', 'from', 'to', 'campus'].includes(key)) params.set(key, value);
      }
      setSearch(params, { replace: true });
    },
    [search, setSearch]
  );
  return [filters, update];
}

/** Load a statistics screen's data whenever its filters change. */
export function useStatisticsData<T>(
  load: (filters: StatisticsFilters) => Promise<T>,
  filters: StatisticsFilters,
  extraKey = ''
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = `${filters.preset}|${filters.from}|${filters.to}|${filters.campus}|${extraKey}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load(filters)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Failed to load statistics'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `load` is a stable module function per page; the key captures everything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  return { data, loading, error, reload: () => setAttempt((n) => n + 1) };
}
